import React from 'react';
import { Routes, Route, Navigate, useLocation, matchPath } from 'react-router-dom';
import type { RouteConfig } from '@his/shared';
import { RouteGuard, setCredentials } from '@his/shared';
import { useAppSelector, useAppDispatch } from '@/stores/hooks';
import { trpc } from '@/lib/trpc';
import { mobileRoutes } from '@/routes';
import { flattenMobileRoutesForTitle } from '@/routes/flatten-mobile-routes';
import { MobileLayout } from '@/components/layout';
import { IconGift as Gift, IconUserCircle as UserRound } from '@tabler/icons-react';

/**
 * 错误边界：捕获渲染错误
 */
class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <h1>出错了</h1>
          <p>{this.state.error?.message}</p>
          <pre
            style={{
              textAlign: 'left',
              background: '#f5f5f5',
              padding: '1rem',
              borderRadius: '4px',
            }}
          >
            {this.state.error?.stack}
          </pre>
        </div>
      );
    }

    return this.props.children;
  }
}

/** 供 document.title 匹配：含嵌套路由展平后的完整 path */
const MOBILE_ROUTE_TITLE_ENTRIES = flattenMobileRoutesForTitle(mobileRoutes);

function stripLeadingSlash(path: string): string {
  return path.startsWith('/') ? path.slice(1) : path;
}

/**
 * App 应用主组件
 * 
 * ✅ 使用 MobileLayout（Page + TabBar）
 * ✅ 受保护路由：RouteGuard 检查认证状态
 * ✅ TabBar 配置：活动和我的两个 Tab
 */
/**
 * 布局包装组件
 * 根据当前路由动态显示 TabBar；顶栏由各页面自行渲染
 */
function LayoutWrapper({ children }: { children: React.ReactNode }) {
  const location = useLocation();

  // 根据路由获取页面标题
  const getPageTitle = (pathname: string): string => {
    // 必须用路由模式匹配；展平后取 path 更长的一条（更具体）。
    let best: (typeof MOBILE_ROUTE_TITLE_ENTRIES)[number] | null = null;
    for (const r of MOBILE_ROUTE_TITLE_ENTRIES) {
      const m = matchPath({ path: r.path, end: true }, pathname);
      if (m && (!best || r.path.length > best.path.length)) {
        best = r;
      }
    }
    return best?.meta?.title || '人文派';
  };

  // 路由切换时同步浏览器标题
  React.useEffect(() => {
    const title = getPageTitle(location.pathname);
    document.title = title;
  }, [location.pathname]);

  // 判断是否需要显示 TabBar（某些页面如详情页不需要）
  const shouldShowTabBar = (pathname: string): boolean => {
    // 排除不需要 TabBar 的路径
    const excludePaths = ['/activities/items', '/activities/', '/activities/:id/posters', '/me/edit'];
    // 检查是否是活动详情相关页面（包括海报页面）
    if (pathname.startsWith('/activities/') && pathname !== '/activities') {
      return false;
    }
    // 编辑资料页面不显示 TabBar
    if (pathname === '/me/edit') {
      return false;
    }
    return !excludePaths.some((path) => pathname.startsWith(path));
  };

  return (
    <MobileLayout
      tabBar={
        shouldShowTabBar(location.pathname)
          ? {
              items: [
                {
                  path: '/activities',
                  icon: Gift,
                  label: '活动',
                  matchSubRoutes: true,
                },
                {
                  path: '/me',
                  icon: UserRound,
                  label: '我的',
                  matchSubRoutes: true,
                },
              ],
            }
          : undefined
      }
    >
      {children}
    </MobileLayout>
  );
}

/**
 * App 应用主组件
 * 
 * ✅ 使用 MobileLayout（Page + TabBar）
 * ✅ 受保护路由：RouteGuard 检查认证状态
 * ✅ TabBar 配置：活动和我的两个 Tab
 */
function renderMobileRoute(
  route: RouteConfig,
  keyPrefix: string,
  opts: { nested: boolean; omitLayout: boolean },
  isAuthenticated: boolean,
  dispatch: ReturnType<typeof useAppDispatch>
): React.ReactNode {
  const { nested, omitLayout } = opts;
  const { element, meta } = route;
  const requiresAuth = meta?.requiresAuth !== false;

  const guarded = requiresAuth ? (
    <RouteGuard
      meta={meta}
      isAuthenticated={isAuthenticated}
      trpc={trpc}
      dispatch={dispatch}
      setCredentials={setCredentials}
      isHomeApp={false}
    >
      {element}
    </RouteGuard>
  ) : (
    element
  );

  const wrapped = omitLayout ? guarded : <LayoutWrapper>{guarded}</LayoutWrapper>;

  if (route.index) {
    return (
      <Route key={keyPrefix} index element={wrapped} />
    );
  }

  const rrPath = nested ? stripLeadingSlash(route.path) : route.path;

  if (!route.children?.length) {
    return (
      <Route key={keyPrefix} path={rrPath} element={wrapped} />
    );
  }

  return (
    <Route key={keyPrefix} path={rrPath} element={wrapped}>
      {route.children.map((child, i) =>
        renderMobileRoute(child, `${keyPrefix}-${i}`, { nested: true, omitLayout: true }, isAuthenticated, dispatch)
      )}
    </Route>
  );
}

function App() {
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);

  return (
    <ErrorBoundary>
      <Routes>
        {mobileRoutes.map((route, i) =>
          renderMobileRoute(route, String(i), { nested: false, omitLayout: false }, isAuthenticated, dispatch)
        )}
        <Route path="*" element={<Navigate to="/activities" replace />} />
      </Routes>
    </ErrorBoundary>
  );
}

export default App;


